// src/components/challenges/MicroChallenges.tsx
// Aligned Actions - Daily invitations for manifestation momentum

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft, Zap, Check, RefreshCw, Sparkles,
  Calendar, ChevronRight
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase/browserClient';
import { useAuth } from '../../hooks/useAuth';
import { useSoulFile } from '../../hooks/useSoulFile';
import { ToolHeader } from '../ui/ToolHeader';

interface AlignedAction {
  id: string;
  title: string;
  description: string;
  category: 'identity' | 'action' | 'mindset' | 'connection';
}

interface CompletedAction {
  id: string;
  user_id: string;
  challenge_id: string;
  completed_at: string;
  reflection: string | null;
}

// Reduced to 12 aligned actions across 4 categories
const ALIGNED_ACTIONS: AlignedAction[] = [
  // Identity (3)
  { id: 'id-1', title: 'Future Self Check-In', description: 'Ask yourself: "What would my future self do right now?" and do it.', category: 'identity' },
  { id: 'id-2', title: 'Act As If', description: 'Make one decision today as if you\'ve already achieved your goal.', category: 'identity' },
  { id: 'id-3', title: 'Identity Statement', description: 'Write "I am someone who..." and complete it with something aligned.', category: 'identity' },

  // Action (3)
  { id: 'act-1', title: 'One Brave Email', description: 'Send one message you\'ve been putting off.', category: 'action' },
  { id: 'act-2', title: '5-Minute Move', description: 'Take 5 minutes of aligned action toward your biggest goal.', category: 'action' },
  { id: 'act-3', title: 'Clear One Thing', description: 'Complete one task that\'s been lingering too long.', category: 'action' },

  // Mindset (3)
  { id: 'mind-1', title: 'Evidence Hunt', description: 'Find 3 pieces of evidence that your manifestation is coming.', category: 'mindset' },
  { id: 'mind-2', title: 'Reframe a Fear', description: 'Take one fear and write down how it could be excitement instead.', category: 'mindset' },
  { id: 'mind-3', title: 'Gratitude Burst', description: 'Write 10 things you\'re grateful for in under 2 minutes.', category: 'mindset' },

  // Connection (3)
  { id: 'conn-1', title: 'Genuine Compliment', description: 'Give a heartfelt, specific compliment to someone.', category: 'connection' },
  { id: 'conn-2', title: 'Reach Out', description: 'Message someone you\'ve been thinking about but haven\'t contacted.', category: 'connection' },
  { id: 'conn-3', title: 'Share Your Vision', description: 'Tell someone about one of your goals or dreams.', category: 'connection' },
];

const CATEGORY_CONFIG = {
  identity: { label: 'Identity', color: 'bg-purple-100/60 text-purple-600 border border-purple-200/40', iconColor: 'text-purple-500' },
  action: { label: 'Action', color: 'bg-purple-100/60 text-purple-600 border border-purple-200/40', iconColor: 'text-purple-500' },
  mindset: { label: 'Mindset', color: 'bg-purple-100/60 text-purple-600 border border-purple-200/40', iconColor: 'text-purple-500' },
  connection: { label: 'Connection', color: 'bg-purple-100/60 text-purple-600 border border-purple-200/40', iconColor: 'text-purple-500' },
};

interface MicroChallengesProps {
  onClose?: () => void;
  embedded?: boolean;
}

export function MicroChallenges({ onClose, embedded = false }: MicroChallengesProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { soulFile } = useSoulFile();

  const [currentAction, setCurrentAction] = useState<AlignedAction | null>(null);
  const [completedToday, setCompletedToday] = useState<string[]>([]);
  const [allCompleted, setAllCompleted] = useState<CompletedAction[]>([]);
  const [showComplete, setShowComplete] = useState(false);
  const [reflection, setReflection] = useState('');
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState<'today' | 'history'>('today');
  const [daysShowingUp, setDaysShowingUp] = useState(0);

  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    if (user?.id) {
      loadCompletedActions();
      selectTodaysAction();
    }
  }, [user?.id]);

  const loadCompletedActions = async () => {
    if (!user?.id) return;

    const { data, error } = await supabase
      .from('completed_challenges')
      .select('*')
      .eq('user_id', user.id)
      .order('completed_at', { ascending: false });

    if (!error && data) {
      setAllCompleted(data);

      // Get today's completed
      const todayCompleted = data
        .filter(c => c.completed_at.startsWith(today))
        .map(c => c.challenge_id);
      setCompletedToday(todayCompleted);

      // Count unique days showing up
      const uniqueDays = new Set(data.map(c => c.completed_at.split('T')[0]));
      setDaysShowingUp(uniqueDays.size);
    }
  };

  const selectTodaysAction = () => {
    // Use date as seed for consistent daily action
    const seed = new Date().toDateString();
    const shuffled = [...ALIGNED_ACTIONS].sort(() => {
      const hash = seed.split('').reduce((a, b) => {
        a = ((a << 5) - a) + b.charCodeAt(0);
        return a & a;
      }, 0);
      return Math.sin(hash) - 0.5;
    });

    setCurrentAction(shuffled[0]);
  };

  const showAnotherAction = () => {
    if (!currentAction) return;

    // Get a different action
    const available = ALIGNED_ACTIONS.filter(a => a.id !== currentAction.id);
    const random = available[Math.floor(Math.random() * available.length)];
    setCurrentAction(random);
  };

  const handleComplete = async () => {
    if (!user?.id || !currentAction) return;
    setSaving(true);

    try {
      await supabase.from('completed_challenges').insert({
        user_id: user.id,
        challenge_id: currentAction.id,
        reflection: reflection.trim() || null,
      });

      // Log activity
      await supabase.from('activity_log').insert({
        user_id: user.id,
        activity_type: 'challenge-completed',
        activity_date: today,
        metadata: { challenge_id: currentAction.id }
      });

      setShowComplete(false);
      setReflection('');
      loadCompletedActions();
    } catch (err) {
      console.error('Error completing action:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (embedded && onClose) {
      onClose();
    } else {
      navigate('/chat');
    }
  };

  const isCompleted = currentAction && completedToday.includes(currentAction.id);
  const catConfig = currentAction ? CATEGORY_CONFIG[currentAction.category] : null;

  const wrapperClass = embedded
    ? "flex-1 overflow-y-auto"
    : "min-h-screen bg-gradient-to-br from-pink-100/40 via-purple-50/40 to-cyan-50/40";

  return (
    <div className={wrapperClass}>
      <ToolHeader
        title="Aligned Actions"
        onBack={handleClose}
        backLabel="Chat"
        rightAction={
          <button
            onClick={() => setView(view === 'today' ? 'history' : 'today')}
            className={`p-2 rounded-xl transition-colors ${view === 'history' ? 'bg-white/50 text-purple-700' : 'text-purple-600 hover:bg-white/30'}`}
          >
            <Calendar className="w-5 h-5" />
          </button>
        }
      />

      <div className="max-w-xl mx-auto px-6">
        {/* Stats Bar */}
        {daysShowingUp > 0 && (
          <div className="bg-white/30 backdrop-blur-md border border-white/50 rounded-2xl p-4 mb-6">
            <div className="flex items-center justify-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-500" />
              <p className="text-purple-800">
                <span className="font-semibold">{daysShowingUp}</span> {daysShowingUp === 1 ? 'day' : 'days'} of showing up for yourself
              </p>
            </div>
          </div>
        )}

        <AnimatePresence mode="wait">
          {view === 'today' ? (
            <motion.div
              key="today"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              <div className="text-center mb-8">
                <p className="text-purple-600 font-medium">Today's Aligned Action</p>
                <p className="text-purple-400 text-sm mt-1">An invitation to show up for yourself</p>
              </div>

              {currentAction && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`bg-white/30 backdrop-blur-md border rounded-2xl p-6 transition-all ${
                    isCompleted ? 'border-green-300/50 shadow-lg shadow-green-500/10' : 'border-white/50'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    {/* Icon bubble instead of empty circle */}
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-100 to-pink-100 border border-white/60 flex items-center justify-center flex-shrink-0">
                      {isCompleted ? (
                        <Check className="w-6 h-6 text-green-600" />
                      ) : (
                        <Sparkles className="w-6 h-6 text-purple-500" />
                      )}
                    </div>

                    <div className="flex-1">
                      <h3 className={`text-lg font-semibold mb-1 ${isCompleted ? 'text-green-700' : 'text-purple-800'}`}>
                        {currentAction.title}
                      </h3>
                      <p className="text-purple-600 text-sm mb-3">{currentAction.description}</p>

                      {catConfig && (
                        <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${catConfig.color}`}>
                          {catConfig.label}
                        </span>
                      )}
                    </div>
                  </div>

                  {isCompleted ? (
                    <div className="bg-green-100/50 rounded-xl p-4 text-center mt-6">
                      <p className="text-green-700 font-medium">✓ You did this today</p>
                      <p className="text-green-600 text-sm mt-1">Look at you showing up ✨</p>
                    </div>
                  ) : (
                    <div className="flex gap-3 mt-6 pt-6 border-t border-white/30">
                      <button
                        onClick={showAnotherAction}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-white/40 text-purple-600 font-medium rounded-xl border border-white/50 hover:bg-white/50 transition-all"
                      >
                        <RefreshCw className="w-4 h-4" />
                        Show me another
                      </button>
                      <button
                        onClick={() => setShowComplete(true)}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-medium rounded-xl shadow-md hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all"
                      >
                        <Check className="w-4 h-4" />
                        I did this
                      </button>
                    </div>
                  )}
                </motion.div>
              )}
            </motion.div>
          ) : (
            <motion.div
              key="history"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <h2 className="text-lg font-semibold text-purple-800 mb-4">Your Journey</h2>

              {allCompleted.length === 0 ? (
                <div className="text-center py-12 text-purple-500">
                  <Zap className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No actions completed yet</p>
                  <p className="text-sm mt-2">Take your first aligned action today</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {allCompleted.slice(0, 20).map((completed) => {
                    const action = ALIGNED_ACTIONS.find(a => a.id === completed.challenge_id);
                    if (!action) return null;

                    return (
                      <div
                        key={completed.id}
                        className="bg-white/30 backdrop-blur-md border border-white/50 rounded-2xl p-4"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="font-medium text-purple-800">{action.title}</h3>
                          <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                        </div>
                        {completed.reflection && (
                          <p className="text-purple-600 text-sm italic mb-2">"{completed.reflection}"</p>
                        )}
                        <p className="text-xs text-purple-400">
                          {new Date(completed.completed_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </p>
                      </div>
                    );
                  })}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Complete Modal */}
      <AnimatePresence>
        {showComplete && currentAction && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50"
            onClick={() => setShowComplete(false)}
          >
            <motion.div
              initial={{ y: 100, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 100, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-t-3xl sm:rounded-3xl w-full sm:max-w-md p-6"
            >
              <div className="text-center mb-6">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center">
                  <Check className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-xl font-semibold text-purple-800">Beautiful</h2>
                <p className="text-purple-600 mt-1">{currentAction.title}</p>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  How did it feel? (optional)
                </label>
                <textarea
                  value={reflection}
                  onChange={(e) => setReflection(e.target.value)}
                  placeholder="Any reflections you want to capture..."
                  rows={3}
                  className="w-full p-4 rounded-xl border border-gray-200 focus:border-purple-400 outline-none resize-none"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowComplete(false)}
                  className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleComplete}
                  disabled={saving}
                  className="flex-1 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-medium disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Done ✓'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default MicroChallenges;
